/**
 * TranscriptSearch — full-text search across all call transcripts.
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import TranscriptPage from './TranscriptPage';

const API = '/api/ai';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

interface SearchResult {
  id: number;
  recording_id: number;
  snippet: string;
  rank: number;
  language: string;
  word_count: number;
  duration_seconds: number;
  created_at: string;
  recorded_at: string;
  filename: string;
  original_name: string;
  username: string;
}

export default function TranscriptSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [selectedRecordingId, setSelectedRecordingId] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string, pg = 1) => {
    if (!q.trim() || q.length < 2) { setResults([]); return; }
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/transcripts/search`, {
        params: { q, page: pg, page_size: 20 },
        ...auth(),
      });
      setResults(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setPage(pg);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value, 1), 400);
  };

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (selectedRecordingId !== null) {
    return (
      <TranscriptPage
        recordingId={selectedRecordingId}
        onBack={() => setSelectedRecordingId(null)}
      />
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', background: '#0f172a', color: '#e2e8f0' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#f1f5f9' }}>🔍 Transcript Search</h2>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
          Search across all call transcripts by keyword, phrase, or topic.
        </p>
      </div>

      {/* Search bar */}
      <div style={{ position: 'relative', marginBottom: 24, maxWidth: 600 }}>
        <input
          value={query}
          onChange={e => handleInput(e.target.value)}
          placeholder="Search transcripts… e.g. 'payment issue' or 'refund'"
          autoFocus
          style={{
            width: '100%', background: '#1e293b', border: '1px solid #334155',
            color: '#e2e8f0', borderRadius: 10, padding: '12px 16px 12px 44px',
            fontSize: 14, boxSizing: 'border-box',
          }}
        />
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 18 }}>🔍</span>
        {loading && (
          <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#6366f1' }}>
            searching…
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#ef444422', border: '1px solid #ef444466', borderRadius: 8, padding: '10px 14px', color: '#fca5a5', marginBottom: 16, fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      {/* Results count */}
      {!loading && query.length >= 2 && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
          {total} result{total !== 1 ? 's' : ''} for "{query}"
        </div>
      )}

      {/* Results list */}
      {results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {results.map(r => (
            <div
              key={r.id}
              onClick={() => setSelectedRecordingId(r.recording_id)}
              style={{
                background: '#1e293b', borderRadius: 10, padding: '16px 18px',
                cursor: 'pointer', border: '1px solid #334155',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = '#6366f1')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = '#334155')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📞 {r.original_name || r.filename}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>
                  {new Date(r.recorded_at || r.created_at).toLocaleDateString()}
                </div>
              </div>

              {/* Snippet with highlights */}
              <div
                style={{ marginTop: 10, fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: r.snippet || '' }}
              />

              <div style={{ marginTop: 10, display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: '#64748b' }}>
                <span>👤 {r.username}</span>
                {r.word_count && <span>📝 {r.word_count} words</span>}
                {r.duration_seconds && <span>⏱ {formatDuration(Number(r.duration_seconds))}</span>}
                {r.language && <span>🌐 {r.language.toUpperCase()}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && query.length >= 2 && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#334155' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔎</div>
          <div style={{ fontSize: 16, color: '#475569' }}>No results for "{query}"</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Try different keywords or check that transcription is enabled.</div>
        </div>
      )}

      {/* Landing state */}
      {query.length < 2 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#334155' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎙</div>
          <div style={{ fontSize: 15, color: '#475569', fontWeight: 600 }}>Search your call transcripts</div>
          <div style={{ fontSize: 13, marginTop: 8, color: '#64748b', lineHeight: 1.6 }}>
            Type at least 2 characters to search across all transcribed calls.<br />
            Results include highlighted matching segments.
          </div>

          {/* Suggestions */}
          <div style={{ marginTop: 24, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {['refund', 'billing', 'technical issue', 'upgrade', 'cancel'].map(s => (
              <button
                key={s}
                onClick={() => { setQuery(s); search(s, 1); }}
                style={{
                  background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
                  borderRadius: 20, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 24 }}>
          <button onClick={() => search(query, page - 1)} disabled={page <= 1} style={pagBtn}>◀ Prev</button>
          <span style={{ padding: '6px 12px', color: '#64748b', fontSize: 13 }}>{page} / {pages}</span>
          <button onClick={() => search(query, page + 1)} disabled={page >= pages} style={pagBtn}>Next ▶</button>
        </div>
      )}
    </div>
  );
}

const pagBtn: React.CSSProperties = {
  background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
  borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
};
